import * as React from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "@src/../entrypoints/theme";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import Grid from "@src/stories/sections/Multicolumn/Layouts/Grid";
import Scrollable from "@src/stories/sections/Multicolumn/Layouts/Scrollable";
import Carousel from "@src/stories/sections/Multicolumn/Layouts/Carousel";
import { sendGtagEvent } from "@src/../js/helpers";

const generatePromotionData = (column: any, index: number, sectionId?: string) => {
  const promotionId = `multicolumn_${sectionId || 'section'}_column_${index + 1}`;
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

  const promotionName =
    column.settings.promotional_name
      ? stripHtml(column.settings.promotional_name)
      : column.settings.title
      ? stripHtml(column.settings.title)
      : column.settings.tagline
      ? stripHtml(column.settings.tagline)
      : `Column ${index + 1}`;

  // console.log('Promotion Name:', promotionName);
  
  const creativeName = `multicolumn_tile_${column.settings.layout || 'default'}`;
  
  // Determine the URL to track - prioritize button_url, then fall back to vimeo links
  let trackingUrl = column.settings.button_url || null;
  if (!trackingUrl && (column.settings.vimeo_link || column.settings.vimeo_link_mobile)) {
    trackingUrl = column.settings.vimeo_link || column.settings.vimeo_link_mobile;
  }
  
  return {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: creativeName,
    creative_slot: `position_${index + 1}`,
    location_id: sectionId || 'multicolumn_section',
    url: trackingUrl
  };
};

const layouts = {
  'grid': Grid,
  'scrollable': Scrollable,
  'carousel': Carousel,
}

export const MultiColumn = ({
  settings,
  columns,
  headerText,
  subtext,
  tagline,
  columns_count,
}) => {
  let isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isLoaded, setIsLoaded] = useState(false);
  const [viewedPromotions, setViewedPromotions] = useState(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  const layout = isMobile ? settings?.section.layout_mobile : settings?.section.layout;
  const LayoutComponent = layouts[layout] || Grid;
  const sectionId = settings?.section?.id || 'multicolumn_section';

  // Track view_promotion events when columns come into viewport
  useEffect(() => {
    if (!isLoaded || !columns?.length) return;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const columnIndex = parseInt(entry.target.getAttribute('data-column-index') || '0');
          const column = columns[columnIndex];
          
          if (column && !viewedPromotions.has(columnIndex)) {
            const promotionData = generatePromotionData(column, columnIndex, sectionId);
            
            // Use existing helper function for consistent GA4 tracking
            sendGtagEvent(
              'view_promotion',
              promotionData.promotion_id,
              promotionData.promotion_name,
              promotionData.creative_name,
              null // No URL navigation for view events
            );

            setViewedPromotions(prev => new Set(prev).add(columnIndex));
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px'
    });

    // Observe all column elements
    const columnElements = sectionRef.current?.querySelectorAll('[data-column-index]');
    columnElements?.forEach(element => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [isLoaded, columns, viewedPromotions, sectionId]);

  // Handle column click tracking with URL navigation
  const handleColumnClick = (column: any, index: number) => {
    const promotionData = generatePromotionData(column, index, sectionId);
    
    // Use existing helper function with URL navigation support
    sendGtagEvent(
      'select_promotion',
      promotionData.promotion_id,
      promotionData.promotion_name,
      promotionData.creative_name,
      promotionData.url // Will handle navigation if URL exists
    );
  };

  useEffect(() => {
    if (isMobile !== undefined) {
      setIsLoaded(true);
    }
  }, [isMobile]);

  if (!isLoaded) {
    return null;
  }

  if (settings.section.render_type !== "all") {
    if (isMobile && settings.section.render_type !== "mobile-only") return;
    if (!isMobile && settings.section.render_type !== "desktop-only") return;
  }

  return (
    <>
      <div ref={sectionRef}>
        <LayoutComponent
            settings={settings}
            columns={columns}
            headerText={headerText}
            subtext={subtext}
            tagline={tagline}
            columns_count={columns_count}
            onColumnClick={handleColumnClick}
            sectionId={sectionId}
        />
      </div>
    </>
  );
};

export default MultiColumn;
